import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    MapPin,
    Search,
    Star,
    Map as MapIcon,
    BookOpen,
    ChevronRight,
    Compass,
    Utensils,
    History,
    Trash2
} from 'lucide-react';
import { guides, restaurants, summerSchools, tutors, allSponsoredAds } from '../data/mockData';
import RequestInfoForm from '../components/RequestInfoForm';
import AdCarousel from '../components/AdCarousel';
import LiveMap from '../components/LiveMap';

const ExploreView = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialTab = queryParams.get('tab') || 'guide';

    const [activeTab, setActiveTab] = useState(initialTab);
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestTarget, setRequestTarget] = useState({ name: '', type: '' });
    const [itinerary, setItinerary] = useState([]);

    const handleRequestClick = (name, type) => {
        setRequestTarget({ name, type });
        setShowRequestForm(true);
    };

    const addToItinerary = (item) => {
        if (!itinerary.find(i => i.id === item.id && i.name === item.name)) {
            setItinerary([...itinerary, item]);
            alert(`${item.name} added to your itinerary!`);
        }
    };

    const removeFromItinerary = (id) => {
        setItinerary(itinerary.filter(i => i.id !== id));
    };

    const getAdsForTab = () => {
        if (activeTab === 'guide') return allSponsoredAds.filter(a => a.category === 'Guides' || a.category === 'Hairstylist');
        if (activeTab === 'food') return allSponsoredAds.filter(a => a.category === 'Restaurant' || a.category === 'Chefs');
        if (activeTab === 'summer') return allSponsoredAds.filter(a => a.category === 'Hotels' || a.category === 'Shortstay');
        return allSponsoredAds.slice(0, 3);
    };

    const displayAds = getAdsForTab();

    // Helper to roughly get coordinates based on area
    const getCoords = (area) => {
        if (area.includes('Victoria Island') || area === 'VI') return [6.4281, 3.4219];
        if (area.includes('Lagos Island')) return [6.4541, 3.4064];
        if (area.includes('Idi-Araba')) return [6.5204, 3.3512];
        if (area.includes('Ikeja')) return [6.6018, 3.3515];
        if (area.includes('Lekki')) return [6.4698, 3.5852];
        return [9.0820, 8.6753]; // Default to Nigeria Center if unknown
    };

    const restaurantMarkers = restaurants.map(r => ({
        position: getCoords(r.area),
        title: r.name,
        description: r.cuisine + ' • ' + r.budget
    }));

    return (
        <div className="explore-view animate-fade">
            <div className="explore-tabs">
                <button className={activeTab === 'guide' ? 'active' : ''} onClick={() => setActiveTab('guide')}>Guide</button>
                <button className={activeTab === 'food' ? 'active' : ''} onClick={() => setActiveTab('food')}>Food</button>
                <button className={activeTab === 'summer' ? 'active' : ''} onClick={() => setActiveTab('summer')}>Stay</button>
                <button className={activeTab === 'itinerary' ? 'active' : ''} onClick={() => setActiveTab('itinerary')}>Itinerary ({itinerary.length})</button>
            </div>

            <AdCarousel providers={displayAds.length > 0 ? displayAds : allSponsoredAds.slice(0, 3)} title="Sponsored Places" />

            <div className="explore-content">
                {activeTab === 'guide' && (
                    <div className="guide-view">
                        <div className="video-spotlight card">
                            <div className="video-container">
                                <iframe
                                    src="https://www.youtube.com/embed/WYsXoCDaD0o"
                                    title="Lagos Survival Guide"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                            <div className="video-info">
                                <span className="v-tag">VIDEO GUIDE</span>
                                <h4>Mastering Lagos: Survival Essentials</h4>
                            </div>
                        </div>

                        <div className="guide-hero">
                            <h1>Lagos Navigator</h1>
                            <p>Everything you need to know about "Eko" (Lagos).</p>
                        </div>
                        {guides.map((guide, i) => (
                            <div key={i} className="guide-card card">
                                <div className="guide-header-box">
                                    <Compass size={18} color="var(--primary-green)" />
                                    <h4>{guide.title}</h4>
                                </div>
                                <p>{guide.content}</p>
                                <div className="guide-actions">
                                    <button className="text-btn">Read More</button>
                                    <button className="secondary-btn sm" onClick={() => addToItinerary({ id: `g-${i}`, name: guide.title, type: 'Guide' })}>Save</button>
                                </div>
                            </div>
                        ))}
                        <div className="state-guides card locked">
                            <h4>Abuja Guide</h4>
                            <span>Coming Soon</span>
                        </div>
                    </div>
                )}

                {activeTab === 'food' && (
                    <div className="food-view">
                        <div className="map-mock-food card" style={{ padding: 0, overflow: 'hidden' }}>
                            <LiveMap 
                                center={[9.0820, 8.6753]} 
                                zoom={6} 
                                markers={restaurantMarkers} 
                            />
                        </div>
                        <div className="food-list">
                            <h3>Top Picks Near You</h3>
                            {restaurants.map(r => (
                                <div key={r.id} className="food-card card">
                                    <div className="food-info">
                                        <h4>{r.name}</h4>
                                        <p>{r.cuisine} • {r.area}</p>
                                        <div className="food-meta">
                                            <Star size={12} fill="var(--gold-verified)" /> {r.rating}
                                            <span className="budget">{r.budget}</span>
                                        </div>
                                    </div>
                                    <button className="primary-btn sm" onClick={() => addToItinerary({ id: r.id, name: r.name, type: 'Food' })}>Add</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'summer' && (
                    <div className="summer-view">
                        <section className="stay-sec">
                            <h3>Summer School Programs</h3>
                            {summerSchools.map(school => (
                                <div key={school.id} className="item-card card">
                                    <div className="item-header">
                                        <h4>{school.name}</h4>
                                        <span>{school.area}</span>
                                    </div>
                                    <div className="item-body">
                                        <p>Ages: {school.ages} | Duration: {school.duration}</p>
                                        <b className="price">{school.tuition}</b>
                                    </div>
                                    <button className="primary-btn full-w" onClick={() => handleRequestClick(school.name, 'Summer Program')}>Request Info</button>
                                </div>
                            ))}
                        </section>

                        <section className="stay-sec">
                            <h3>Private Tutors</h3>
                            {tutors.map(tutor => (
                                <div key={tutor.id} className="item-card card">
                                    <div className="item-header-p">
                                        <img src={tutor.photo} alt={tutor.name} className="mini-avatar" />
                                        <div>
                                            <h4>{tutor.name}</h4>
                                            <p>{tutor.education}</p>
                                        </div>
                                    </div>
                                    <p className="subs">{tutor.subjects.join(', ')}</p>
                                    <div className="item-footer">
                                        <b className="price">₦{tutor.rate}/hr</b>
                                        <button className="secondary-btn sm" onClick={() => handleRequestClick(tutor.name, 'Tutoring')}>Book Info</button>
                                    </div>
                                </div>
                            ))}
                        </section>
                    </div>
                )}

                {activeTab === 'itinerary' && (
                    <div className="itinerary-view">
                        <h2>My Lagos Itinerary</h2>
                        {itinerary.length === 0 ? (
                            <div className="empty-itinerary">
                                <p>Your itinerary is empty. Start exploring to save places and guides.</p>
                                <button className="primary-btn" onClick={() => setActiveTab('guide')}>Browse Guide</button>
                            </div>
                        ) : (
                            <div className="itinerary-list">
                                {itinerary.map(item => (
                                    <div key={item.id} className="it-card card">
                                        <div className="it-id">
                                            <span className="it-type">{item.type}</span>
                                            <h4>{item.name}</h4>
                                        </div>
                                        <button className="delete-btn" onClick={() => removeFromItinerary(item.id)}><Trash2 size={16} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <RequestInfoForm
                isOpen={showRequestForm}
                onClose={() => setShowRequestForm(false)}
                entityName={requestTarget.name}
                entityType={requestTarget.type}
            />

            <style dangerouslySetInnerHTML={{
                __html: `
        .explore-tabs { display: flex; gap: 8px; margin-bottom: 24px; padding: 4px; background: #f0f0f0; border-radius: var(--radius-md); overflow-x: auto; scrollbar-width: none; }
        .explore-tabs button { flex: 1; min-width: 80px; padding: 10px; border-radius: 8px; font-weight: 700; color: var(--text-light); }
        .explore-tabs button.active { background: white; color: var(--primary-green); box-shadow: 0 2px 6px rgba(0,0,0,0.05); }

        .video-spotlight { padding: 0; overflow: hidden; margin-bottom: 24px; border: 1px solid var(--border-gray); }
        .video-container { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; }
        .video-container iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
        .video-info { padding: 16px; background: white; }
        .v-tag { font-size: 10px; font-weight: 800; color: var(--primary-green); background: var(--light-green); padding: 2px 8px; border-radius: 4px; margin-bottom: 6px; display: inline-block; }
        .video-info h4 { margin: 0; font-size: 16px; color: var(--text-dark); }

        .guide-hero { margin-bottom: 24px; }
        .guide-hero h1 { font-size: 26px; }
        .guide-hero p { color: var(--text-light); font-size: 14px; }
        .guide-card { margin-bottom: 16px; border: 1px solid var(--border-gray); }
        .guide-header-box { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
        .guide-header-box h4 { margin: 0; font-size: 16px; }
        .guide-card p { font-size: 13px; color: var(--text-light); line-height: 1.5; }
        .guide-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; }
        .text-btn { background: none; color: var(--primary-green); font-size: 13px; font-weight: 700; padding: 0; }
        
        .locked { opacity: 0.6; pointer-events: none; border-style: dashed; }
        .locked h4 { margin-bottom: 4px; }
        .locked span { font-size: 10px; background: var(--border-gray); padding: 2px 8px; border-radius: 4px; }

        .map-mock-food { height: 180px; background: #e5e7eb; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; margin-bottom: 24px; }
        .map-overlay-text { text-align: center; color: var(--text-light); font-size: 14px; z-index: 1; }
        .map-pin-food { position: absolute; width: 20px; height: 20px; background: var(--emergency-red); border-radius: 50% 50% 50% 0; transform: rotate(-45deg); display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        .map-pin-food svg { transform: rotate(45deg); }

        .food-list h3 { font-size: 18px; margin-bottom: 16px; }
        .food-card { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .food-info h4 { font-size: 15px; margin-bottom: 2px; }
        .food-info p { font-size: 12px; color: var(--text-light); margin: 0; }
        .food-meta { display: flex; align-items: center; gap: 8px; font-size: 12px; margin-top: 4px; font-weight: 700; }
        .budget { color: var(--primary-green); }

        .stay-sec { margin-bottom: 30px; }
        .stay-sec h3 { font-size: 18px; margin-bottom: 16px; }
        .item-card { margin-bottom: 16px; }
        .item-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
        .item-header h4 { font-size: 16px; }
        .item-header span { font-size: 11px; color: var(--text-light); font-weight: 700; }
        .item-body { margin-bottom: 16px; }
        .item-body p { font-size: 12px; color: var(--text-light); margin-bottom: 4px; }
        
        .item-header-p { display: flex; gap: 12px; margin-bottom: 12px; }
        .mini-avatar { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; }
        .item-header-p p { font-size: 12px; color: var(--text-light); margin: 0; }
        .subs { font-size: 12px; font-style: italic; margin-bottom: 12px; color: var(--text-dark); }
        .item-footer { display: flex; justify-content: space-between; align-items: center; }
        .price { color: var(--primary-green); font-size: 16px; }

        .itinerary-view h2 { font-size: 24px; margin-bottom: 24px; }
        .empty-itinerary { text-align: center; padding: 40px 0; display: flex; flex-direction: column; gap: 16px; align-items: center; }
        .it-card { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .it-type { font-size: 9px; font-weight: 800; color: var(--primary-green); background: var(--light-green); padding: 2px 6px; border-radius: 4px; text-transform: uppercase; margin-bottom: 4px; display: inline-block; }
        .delete-btn { background: none; color: #ccc; }
        .delete-btn:hover { color: var(--emergency-red); }
      `}} />
        </div>
    );
};

export default ExploreView;
